import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import type { Deal, PipelineStage, TeamMember } from '../../types';
import DealCard from './DealCard';

interface PipelineKanbanProps {
  stages: PipelineStage[];
  deals: Deal[];
  members: TeamMember[];
  onDealClick: (deal: Deal) => void;
  onDealDrop: (dealId: string, newStage: string) => void;
  onAddDeal: (stage: string) => void;
}

const PipelineKanban: React.FC<PipelineKanbanProps> = ({
  stages,
  deals,
  members,
  onDealClick,
  onDealDrop,
  onAddDeal,
}) => {
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const getDealsByStage = (stageKey: string) => {
    return deals.filter((d) => d.stage === stageKey);
  };

  const handleDragStart = (e: React.DragEvent, dealId: string) => {
    e.dataTransfer.setData('dealId', dealId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    if (dealId) {
      onDealDrop(dealId, stageKey);
    }
    setDragOverStage(null);
  };

  const getStageColour = (stage: PipelineStage): string => {
    if (stage.is_won) return 'bg-emerald-500';
    if (stage.is_lost) return 'bg-red-500';
    return 'bg-slate-500';
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 h-full">
      {stages.map((stage) => {
        const stageDeals = getDealsByStage(stage.key);
        const isDragOver = dragOverStage === stage.key;

        return (
          <div
            key={stage.id}
            className="flex-shrink-0 w-72 flex flex-col"
            onDragOver={(e) => handleDragOver(e, stage.key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage.key)}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getStageColour(stage)}`} />
                <h3 className="font-medium text-slate-200 text-sm">{stage.label}</h3>
                <span className="text-xs text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                  {stageDeals.length}
                </span>
              </div>
              <button
                onClick={() => onAddDeal(stage.key)}
                className="p-1 text-slate-500 hover:text-white hover:bg-slate-700 rounded transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>

            <div
              className={`flex-1 bg-slate-900/50 rounded-lg p-2 space-y-2 min-h-[200px] transition-colors ${
                isDragOver ? 'bg-slate-800/50 ring-2 ring-orange-500/50' : ''
              }`}
            >
              {stageDeals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  members={members}
                  onClick={() => onDealClick(deal)}
                  onDragStart={(e) => handleDragStart(e, deal.id)}
                />
              ))}

              {stageDeals.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No deals
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PipelineKanban;
